import { isGroupedByEvent, type SearchParameters } from '../searchParameters';
import type { SearchMode } from './searchMode';
import { SearchModeEnum } from './searchMode';

/**
 * Derives the kind of search a set of parameters describes.
 *
 * SearchParameters is the input, not the owner: a mode is also implied by the
 * route that started the search, so the concept stays separate from the
 * parameter values it is read from.
 *
 * Which parameters a mode leaves available is a separate question, answered by
 * `availableSearchParametersFor` rather than by anything on this class.
 */
export class SearchModeFactory {
  fromSearchParameters = (params: SearchParameters): SearchMode => ({
    kind: this.#kindFor(params),
  });

  // Refactoring #kindFor to have less if statements
  // would result in harder to read code.
  // eslint-disable-next-line complexity
  #kindFor(params: SearchParameters): SearchModeEnum {
    // With an events filter set this falls through to EVENT, which then hides
    // groupbyevent (see isGroupedByEvent).
    if (isGroupedByEvent(params)) return SearchModeEnum.GROUP_BY_EVENT;
    const eventArtistMode = this.#eventArtistModeFor(params);
    if (eventArtistMode) return eventArtistMode;
    if (params.stackid) return SearchModeEnum.STACK;
    if (params.recentlyviewed) return SearchModeEnum.RECENTLY_VIEWED;
    if (params.curatedset) return SearchModeEnum.CURATED_SET;
    if (params.morelikethis) return SearchModeEnum.MORE_LIKE_THIS;
    if (params.searchByImage) return SearchModeEnum.SEARCH_BY_IMAGE;
    if (params.contributor) return SearchModeEnum.CONTRIBUTOR;
    return SearchModeEnum.STANDARD;
  }

  #eventArtistModeFor(params: SearchParameters): SearchModeEnum | undefined {
    const isEvent = !!params.events;
    const isArtist = !!params.artistexact;
    if (isEvent && isArtist) return SearchModeEnum.EVENT_ARTIST;
    if (isEvent) return SearchModeEnum.EVENT;
    if (isArtist) return SearchModeEnum.ARTIST;
    return undefined;
  }
}
